import { useApolloClient, useMutation } from "@apollo/client/react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router";

import { constantsData, statusTypesData, urlsData } from "@/data";
import { createUserMutation, getUsersListQuery } from "@/graph";
import { openAlert } from "@/redux/actions";
import { displayErrorHandler } from "@/utils";

const useCreateUser = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const client = useApolloClient();

    const [createUserAction, { loading }] = useMutation(createUserMutation);

    const createUser = async ({ email, familyName, givenName, role }) => {
        try {
            await createUserAction({
                onCompleted: ({ createUserWithPassword: newUser }) => {
                    if (newUser?.user_id) {
                        const variables = {
                            limit: 25,
                            page: 0,
                        };

                        let existing;
                        try {
                            existing = client.cache.readQuery({
                                query: getUsersListQuery,
                                variables,
                            });
                        } catch {
                            existing = null;
                        }

                        const existingPosts = existing?.listUsers?.posts ?? [];

                        if (!existingPosts.some((u) => u?.user_id === newUser.user_id)) {
                            client.cache.writeQuery({
                                query: getUsersListQuery,
                                variables,
                                data: {
                                    listUsers: {
                                        ...(existing?.listUsers ?? {}),
                                        posts: [newUser, ...existingPosts],
                                        meta: {
                                            ...(existing?.listUsers?.meta ?? {}),
                                            nodeCount: (existing?.listUsers?.meta?.nodeCount ?? 0) + 1,
                                        },
                                    },
                                },
                            });
                        }
                    }

                    navigate(urlsData.routes.users.url);

                    dispatch(
                        openAlert(
                            t("messages.createMsgs.success", { entity: t("users.entity") }),
                            statusTypesData.success,
                        ),
                    );
                },
                variables: {
                    data: {
                        email,
                        family_name: familyName,
                        given_name: givenName,
                        role_id: role,
                    },
                },
            });
        } catch (err) {
            displayErrorHandler(
                err.message === constantsData.failedToFetch ? t("messages.networkFailure") : err.message,
                dispatch,
                openAlert,
            );
        }
    };

    return {
        createUser,
        loading,
    };
};

export default useCreateUser;
